import { useState } from 'react'
import type { ConversationTurn } from '../../api/client'
import type { DocRef } from '../../lib/documentViewer'
import { renderContent } from '../../plugins/renderContent'
import { extractImageMarkers, ImageThumbnails } from '../../plugins/imageAttach'
import { DocAttachments } from '../../plugins/docAttach'
import { renderToolCard, renderToolResult } from './ToolCard'
import { getToolAction } from '../../agents/toolAction'

interface TurnItemProps {
  turn: ConversationTurn
  senderLabel?: string
  onInput?: (text: string) => void
  onRetry?: (turn: ConversationTurn) => void
  onDismiss?: (id: string) => void
  /** This user turn was sent while the agent was still working → show a "queued" badge. */
  queued?: boolean
  /** Owning chat — lets attachment cards scope their file fetch to the right cwd. */
  chatId?: string
  /** v3 views: render tool_call / tool_result as compact, expandable rich cards (diff/code/
   *  command/...) folded to one line. Off (default) keeps the legacy generic tool rows. */
  richTools?: boolean
  /** Agent plugin name (e.g. 'claude') — selects the per-agent tool mapper (agents/<name>.ts). */
  agent?: string
}

/** A .md file a tool-call wrote/read → a DocRef ("the AI sent you a file"), else null.
 *  Agent-agnostic (C4b): instead of a hardcoded tool-NAME set + manual JSON field probing, ask
 *  the agent's tool mapper (agents/<name>.ts) to normalize the call. Any file-touching action
 *  (edit/write/read) exposes a `.file`; we surface it as a document card when it's a .md. A new
 *  agent whose mapper yields these kinds gets doc cards automatically — no edit to this file. */
function docRefFromToolCall(turn: ConversationTurn, agent?: string): DocRef | null {
  const action = getToolAction(agent, turn)
  if (!action || (action.kind !== 'edit' && action.kind !== 'write' && action.kind !== 'read')) return null
  const fp = action.file
  if (!fp || !/\.md$/i.test(fp)) return null
  return { kind: 'file', name: fp.split('/').pop() || fp, src: fp }
}

export function TurnItem({ turn, senderLabel, onInput, onRetry, onDismiss, queued, chatId, richTools, agent }: TurnItemProps) {
  switch (turn.role) {
    case 'user': {
      // Hide any attached-image markers (raw paths) and render thumbnails instead.
      const { paths, clean } = extractImageMarkers(turn.content)
      // รอคิว = live-presence จาก view (queued prop) หรือ optimistic ที่ส่งตอน agent ทำงาน
      // (status 'queued' — ช่วงสั้นๆ ก่อน JSONL confirm แล้ว live-presence รับช่วงต่อ)
      const isQueued = (queued || turn.status === 'queued') && turn.status !== 'failed'
      return (
        <div className={`turn-user${turn.status === 'failed' ? ' turn-user--failed' : ''}${turn.status === 'sending' && !isQueued ? ' turn-user--sending' : ''}${isQueued ? ' turn-user--queued' : ''}`}>
          <div className="turn-user-label">Me</div>
          {clean && <div className="turn-user-bubble">{clean}</div>}
          {paths.length > 0 && <ImageThumbnails paths={paths} />}
          {isQueued && (
            <div className="turn-queued-badge">⏳ รอคิว</div>
          )}
          {/* ส่งแล้วแต่ยังไม่ยืนยันใน JSONL → indicator ชัดเจน (ไม่ใช่ committed เงียบๆ) */}
          {turn.status === 'sending' && !isQueued && (
            <div className="turn-sending-badge">◌ กำลังส่ง…</div>
          )}
          {turn.status === 'failed' && (
            <div className="turn-failed-row">
              <span className="turn-failed-msg">⚠ ส่งไม่สำเร็จ</span>
              <button className="turn-failed-btn" onClick={() => onRetry?.(turn)}>ส่งอีกครั้ง</button>
              <button className="turn-failed-btn turn-failed-btn--danger" onClick={() => onDismiss?.(turn.id)}>ลบ</button>
            </div>
          )}
        </div>
      )
    }

    case 'assistant':
      return (
        <div className="turn-assistant">
          <div className="turn-label">{senderLabel ?? 'Assistant'}</div>
          <div className="turn-content">{renderContent(turn.content, onInput, chatId)}</div>
        </div>
      )

    case 'thinking':
      return <ThinkingTurn turn={turn} />

    case 'tool_call':
      return <ToolCallTurn turn={turn} chatId={chatId} rich={richTools} agent={agent} />

    case 'tool_result':
      return richTools
        ? <div className="turn-tool-result">{renderToolResult(turn)}</div>
        : <ToolResultTurn turn={turn} />

    case 'info':
      return <div className="turn-info">{turn.content}</div>

    default:
      return null
  }
}

// ─── Sub-components with their own useState ───────────────────────────────────

function ThinkingTurn({ turn }: { turn: ConversationTurn }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="turn-thinking">
      <button className="turn-thinking-toggle" onClick={() => setExpanded(v => !v)}>
        {expanded ? '▾' : '▸'} Thinking...
      </button>
      {expanded && <pre className="turn-thinking-content">{turn.content}</pre>}
    </div>
  )
}

function ToolCallTurn({ turn, chatId, rich, agent }: { turn: ConversationTurn; chatId?: string; rich?: boolean; agent?: string }) {
  const [expanded, setExpanded] = useState(false)

  // v3 rich mode: render a compact, expandable card per tool (Edit→diff, Write→code, Bash→cmd,
  // Read/Grep/…→args) via the agent's tool mapper. null only when nothing structured → legacy.
  if (rich) {
    const card = renderToolCard(turn, agent)
    if (card) return <div className="turn-tool turn-tool--rich">{card}</div>
  }

  // Path A: a tool that wrote/read a .md file shows up as an openable document card —
  // "the AI sent you a file" — instead of a raw ⚡ tool row. (If the file turns out not
  // to exist, the card hides itself and we fall through to the plain tool row below.)
  const docRef = docRefFromToolCall(turn, agent)
  if (docRef) {
    return (
      <div className="turn-tool turn-tool--doc">
        <DocAttachments refs={[docRef]} chatId={chatId} />
      </div>
    )
  }

  return (
    <div className="turn-tool">
      <button className="turn-tool-header" onClick={() => setExpanded(v => !v)}>
        {expanded ? '▾' : '▸'} ⚡ {turn.toolName || turn.content}
      </button>
      {expanded && turn.toolInput && (
        <pre className="turn-tool-input">
          {(() => {
            try {
              return JSON.stringify(JSON.parse(turn.toolInput), null, 2)
            } catch {
              return turn.toolInput
            }
          })()}
        </pre>
      )}
    </div>
  )
}

function ToolResultTurn({ turn }: { turn: ConversationTurn }) {
  const [expanded, setExpanded] = useState(false)
  const { paths: imagePaths } = extractImageMarkers(turn.content)
  return (
    <div className="turn-tool-result">
      <div className="turn-tool-result-text">
        {expanded
          ? turn.content
          : turn.content.slice(0, 300) + (turn.content.length > 300 ? '...' : '')}
      </div>
      {turn.content.length > 300 && (
        <button className="turn-expand-btn" onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
      {imagePaths.length > 0 && <ImageThumbnails paths={imagePaths} />}
    </div>
  )
}
